export type TreeRelationshipDomain =
  | 'structural'
  | 'modern-family'
  | 'chosen-family'
  | 'display'
  | 'layout'
  | 'governance';

export type TreeRelationshipClass =
  | 'parentage'
  | 'derived-kinship'
  | 'partnership'
  | 'former-partnership'
  | 'co-parent'
  | 'step-family'
  | 'in-law'
  | 'guardian'
  | 'foster-family'
  | 'god-family'
  | 'chosen-family'
  | 'affiliated-family'
  | 'display'
  | 'layout'
  | 'governance';

export type TreeFamilyAdmissionAuthority =
  | 'STRUCTURAL_DERIVATION'
  | 'EXPLICIT_GOVERNED'
  | 'NONE';

export type TreeRelationshipTransitivity =
  | 'STRUCTURAL_DERIVATION_ONLY'
  | 'NON_TRANSITIVE'
  | 'NOT_APPLICABLE';

export type TreeRelationshipLifecycleAfterPartnershipTermination =
  | 'INDEPENDENT'
  | 'TRANSITION_TO_HISTORY'
  | 'HISTORICAL_PRESERVED'
  | 'SURVIVES_WHILE_SHARED_PARENTAGE_EXISTS'
  | 'EXPLICIT_RETENTION_REQUIRED'
  | 'NOT_APPLICABLE';

export type TreeRelationshipConsentAuthority =
  | 'SEPARATE_WRITE_AUTHORITY_REQUIRED'
  | 'NOT_APPLICABLE';

export type TreeRelationshipDeletionAuthority =
  | 'RELATIONSHIP_ONLY_NEVER_PERSON_DELETE'
  | 'NOT_APPLICABLE';

export interface TreeRelationshipAuthorityRecord {
  readonly relationship: string;
  readonly relationshipClass: TreeRelationshipClass;
  readonly domain: TreeRelationshipDomain;
  readonly authority: 'Tree Governor';
  readonly governed: true;

  /**
   * Compatibility field retained for existing callers.
   *
   * Its canonical meaning is now relationship-edge ancestry authority.
   * Only parent-child edges may return true.
   */
  readonly createsAncestry: boolean;

  /**
   * Whether this relationship edge itself is a structural ancestry edge.
   *
   * This is deliberately distinct from a workflow being authorized to
   * create or update separate parent-child evidence.
   */
  readonly edgeCreatesAncestry: boolean;

  /**
   * Whether a governed workflow for this relationship class may need to
   * mutate separate parent-child evidence to represent the relationship.
   *
   * Example: sibling remains a lateral edge, but a sibling workflow may
   * establish shared parentage through parent-child edges.
   */
  readonly workflowMayAuthorizeParentageMutation: boolean;

  readonly inheritsGeneration: boolean;
  readonly inheritsBranch: boolean;

  readonly admissionAuthority: TreeFamilyAdmissionAuthority;
  readonly transitivity: TreeRelationshipTransitivity;

  readonly lifecycleAfterPartnershipTermination:
    TreeRelationshipLifecycleAfterPartnershipTermination;

  /**
   * A relationship class never manufactures consent authority.
   *
   * Claim ownership, actor authority, participant consent, guardian/legal
   * authority, or other required consent must be proven by the writer that
   * executes the relationship transition.
   */
  readonly consentAuthority: TreeRelationshipConsentAuthority;

  /**
   * Ending or detaching a relationship never authorizes person deletion.
   */
  readonly deletionAuthority: TreeRelationshipDeletionAuthority;

  readonly notes: string;
}

type RelationshipRecordInput = Omit<
  TreeRelationshipAuthorityRecord,
  'authority' | 'governed' | 'createsAncestry'
>;

const record = (
  input: RelationshipRecordInput,
): TreeRelationshipAuthorityRecord =>
  Object.freeze({
    ...input,
    authority: 'Tree Governor',
    governed: true,
    createsAncestry:
      input.edgeCreatesAncestry,
  });

const relationship = (
  input: Omit<
    RelationshipRecordInput,
    'admissionAuthority' |
    'consentAuthority' |
    'deletionAuthority'
  > & {
    readonly admissionAuthority?:
      TreeFamilyAdmissionAuthority;
  },
): TreeRelationshipAuthorityRecord =>
  record({
    ...input,
    admissionAuthority:
      input.admissionAuthority ??
      'EXPLICIT_GOVERNED',
    consentAuthority:
      'SEPARATE_WRITE_AUTHORITY_REQUIRED',
    deletionAuthority:
      'RELATIONSHIP_ONLY_NEVER_PERSON_DELETE',
  });

const nonRelationship = (
  input: Omit<
    RelationshipRecordInput,
    'admissionAuthority' |
    'consentAuthority' |
    'deletionAuthority' |
    'transitivity' |
    'lifecycleAfterPartnershipTermination' |
    'workflowMayAuthorizeParentageMutation'
  >,
): TreeRelationshipAuthorityRecord =>
  record({
    ...input,
    admissionAuthority: 'NONE',
    transitivity: 'NOT_APPLICABLE',
    lifecycleAfterPartnershipTermination:
      'NOT_APPLICABLE',
    consentAuthority: 'NOT_APPLICABLE',
    deletionAuthority: 'NOT_APPLICABLE',
    workflowMayAuthorizeParentageMutation:
      false,
  });

/**
 * CC-TREE-LIVE-003G3C
 *
 * RELATIONSHIP_EDGE_CREATES_ANCESTRY
 *   !=
 * WORKFLOW_MAY_AUTHORIZE_PARENTAGE_MUTATION
 *
 * A lateral/derived relationship may require a governed workflow to add
 * parent-child evidence, but the lateral edge itself never becomes ancestry.
 *
 * FAMILY_ADMISSION_IS_BOUNDED
 * NON_ANCESTRAL_AFFINITY_IS_NON_TRANSITIVE
 * PARTNERSHIP_LIFECYCLE_IS_NOT_PERSON_LIFECYCLE
 * RELATIONSHIP_DETACHMENT_NEVER_AUTHORIZES_PERSON_DELETION
 * RELATIONSHIP_CLASS_GRANTS_ZERO_CONSENT_AUTHORITY
 */
export const TREE_RELATIONSHIP_AUTHORITIES:
readonly TreeRelationshipAuthorityRecord[] =
Object.freeze([
  relationship({
    relationship:
      'biological-parent-child',
    relationshipClass: 'parentage',
    domain: 'structural',
    edgeCreatesAncestry: true,
    workflowMayAuthorizeParentageMutation:
      true,
    inheritsGeneration: true,
    inheritsBranch: true,
    admissionAuthority:
      'STRUCTURAL_DERIVATION',
    transitivity:
      'STRUCTURAL_DERIVATION_ONLY',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'parent_ids is structural ancestry truth; parentage survives partnership lifecycle changes.',
  }),

  relationship({
    relationship:
      'adopted-parent-child',
    relationshipClass: 'parentage',
    domain: 'structural',
    edgeCreatesAncestry: true,
    workflowMayAuthorizeParentageMutation:
      true,
    inheritsGeneration: true,
    inheritsBranch: true,
    admissionAuthority:
      'STRUCTURAL_DERIVATION',
    transitivity:
      'STRUCTURAL_DERIVATION_ONLY',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Adopted parentage is structural ancestry and survives partnership lifecycle changes.',
  }),

  relationship({
    relationship: 'sibling',
    relationshipClass:
      'derived-kinship',
    domain: 'structural',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      true,
    inheritsGeneration: true,
    inheritsBranch: true,
    admissionAuthority:
      'STRUCTURAL_DERIVATION',
    transitivity:
      'STRUCTURAL_DERIVATION_ONLY',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Sibling is lateral and derived from shared parentage. A sibling workflow may establish shared parent-child evidence, but the sibling edge itself never creates ancestry.',
  }),

  relationship({
    relationship:
      'ancestor-descendant',
    relationshipClass:
      'derived-kinship',
    domain: 'structural',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      true,
    inheritsGeneration: true,
    inheritsBranch: true,
    admissionAuthority:
      'STRUCTURAL_DERIVATION',
    transitivity:
      'STRUCTURAL_DERIVATION_ONLY',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Ancestor/descendant kinship is derived from a chain of parent-child edges; a workflow may add a separately governed parent-child edge.',
  }),

  relationship({
    relationship:
      'cousin-and-removal',
    relationshipClass:
      'derived-kinship',
    domain: 'structural',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      true,
    inheritsGeneration: true,
    inheritsBranch: true,
    admissionAuthority:
      'STRUCTURAL_DERIVATION',
    transitivity:
      'STRUCTURAL_DERIVATION_ONLY',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Cousin degree/removal is derived from common-ancestor parentage. A workflow may establish separate parent-child evidence without making the cousin edge ancestry.',
  }),

  relationship({
    relationship: 'spouse-partner',
    relationshipClass: 'partnership',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: true,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'TRANSITION_TO_HISTORY',
    notes:
      'Current partnership is lateral. Ending it must transition partnership history rather than erase people, parentage, co-parenting, or descendants.',
  }),

  relationship({
    relationship:
      'former-spouse-partner',
    relationshipClass:
      'former-partnership',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: true,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'HISTORICAL_PRESERVED',
    notes:
      'Former partnership remains historical family evidence without rendering an active partnership marker.',
  }),

  relationship({
    relationship: 'co-parent',
    relationshipClass: 'co-parent',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: true,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'SURVIVES_WHILE_SHARED_PARENTAGE_EXISTS',
    notes:
      'Co-parent authority is child-mediated and independent from romantic partnership status.',
  }),

  relationship({
    relationship: 'step-family',
    relationshipClass: 'step-family',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: true,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'EXPLICIT_RETENTION_REQUIRED',
    notes:
      'Step-family is non-ancestral. Partnership termination cannot silently preserve or destroy the relationship; retention requires explicit authority.',
  }),

  relationship({
    relationship: 'in-law',
    relationshipClass: 'in-law',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: true,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'EXPLICIT_RETENTION_REQUIRED',
    notes:
      'In-law authority is connector-bounded and non-transitive. Retention after partnership termination must be explicit.',
  }),

  relationship({
    relationship: 'guardian',
    relationshipClass: 'guardian',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Guardian authority is independent from partnership and does not imply ancestry unless a separate parentage edge exists.',
  }),

  relationship({
    relationship: 'foster-family',
    relationshipClass:
      'foster-family',
    domain: 'modern-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Foster-family authority is independent from partnership and non-transitive by default.',
  }),

  relationship({
    relationship: 'god-family',
    relationshipClass: 'god-family',
    domain: 'chosen-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'God-family never creates ancestry, branch authority, or transitive family admission.',
  }),

  relationship({
    relationship: 'chosen-family',
    relationshipClass:
      'chosen-family',
    domain: 'chosen-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: true,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Chosen family receives explicit governed placement and remains non-transitive.',
  }),

  relationship({
    relationship:
      'affiliated-family',
    relationshipClass:
      'affiliated-family',
    domain: 'chosen-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: false,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Affiliated family grants no ancestry, generation, branch, or transitive admission authority.',
  }),

  relationship({
    relationship: 'family-friend',
    relationshipClass:
      'affiliated-family',
    domain: 'chosen-family',
    edgeCreatesAncestry: false,
    workflowMayAuthorizeParentageMutation:
      false,
    inheritsGeneration: false,
    inheritsBranch: false,
    transitivity: 'NON_TRANSITIVE',
    lifecycleAfterPartnershipTermination:
      'INDEPENDENT',
    notes:
      'Family-friend authority is explicit, non-ancestral, non-transitive, and independent from partnership.',
  }),

  nonRelationship({
    relationship:
      'custom-family-title',
    relationshipClass: 'display',
    domain: 'display',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'A visible title never changes structural or lifecycle truth.',
  }),

  nonRelationship({
    relationship:
      'nickname-tree-display',
    relationshipClass: 'display',
    domain: 'display',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'Real name remains mandatory; nickname presentation grants zero relationship authority.',
  }),

  nonRelationship({
    relationship:
      'recursive-branch-isolation',
    relationshipClass: 'layout',
    domain: 'layout',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: true,
    notes:
      'Every parent branch owns one contiguous interval with nested sub-branches.',
  }),

  nonRelationship({
    relationship:
      'family-cluster-ownership',
    relationshipClass: 'layout',
    domain: 'layout',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: true,
    notes:
      'No member from another branch may be inserted inside a cluster interval.',
  }),

  nonRelationship({
    relationship:
      'couple-coparent-unit',
    relationshipClass: 'layout',
    domain: 'layout',
    edgeCreatesAncestry: false,
    inheritsGeneration: true,
    inheritsBranch: true,
    notes:
      'Couples and co-parents remain inside the connected child or parent cluster without changing relationship semantics.',
  }),

  nonRelationship({
    relationship:
      'deterministic-cluster-color',
    relationshipClass: 'layout',
    domain: 'layout',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: true,
    notes:
      'Cluster color is stable presentation evidence and never the only branch signal.',
  }),

  nonRelationship({
    relationship: 'needs-connection',
    relationshipClass: 'governance',
    domain: 'governance',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'Unprovable relationships remain unplaced instead of receiving invented truth.',
  }),

  nonRelationship({
    relationship:
      'mutation-authority',
    relationshipClass: 'governance',
    domain: 'governance',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'All Tree writers must ultimately route through governed mutation authority.',
  }),

  nonRelationship({
    relationship:
      'repair-reconciliation',
    relationshipClass: 'governance',
    domain: 'governance',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'Repairs must be global, evidenced, reversible where safe, and verified.',
  }),

  nonRelationship({
    relationship:
      'command-center-observability',
    relationshipClass: 'governance',
    domain: 'governance',
    edgeCreatesAncestry: false,
    inheritsGeneration: false,
    inheritsBranch: false,
    notes:
      'Tree Governor status, violations, lifecycle transitions, repairs, and certification must be observable.',
  }),
]);

export function getTreeRelationshipAuthorities():
readonly TreeRelationshipAuthorityRecord[] {
  return TREE_RELATIONSHIP_AUTHORITIES;
}

export function getTreeRelationshipAuthority(
  relationship: string,
): TreeRelationshipAuthorityRecord | null {
  return (
    TREE_RELATIONSHIP_AUTHORITIES.find(
      entry =>
        entry.relationship ===
        relationship,
    ) ?? null
  );
}

export function getTreeRelationshipAuthoritiesByClass(
  relationshipClass: TreeRelationshipClass,
): readonly TreeRelationshipAuthorityRecord[] {
  return Object.freeze(
    TREE_RELATIONSHIP_AUTHORITIES.filter(
      entry =>
        entry.relationshipClass ===
        relationshipClass,
    ),
  );
}

export type TreePartnershipLifecycleIntent =
  | 'CREATE_CURRENT'
  | 'END_CURRENT';

export type TreePartnershipLifecycleEndStatus =
  | 'former'
  | 'divorced'
  | 'separated'
  | 'ended';

export type TreePartnershipLifecyclePlanStatus =
  | 'AUTHORIZED_CREATE_PLAN'
  | 'AUTHORIZED_END_PLAN'
  | 'ALREADY_CURRENT'
  | 'REJECTED_AUTH_REQUIRED'
  | 'REJECTED_VIEWER_IDENTITY'
  | 'REJECTED_MISSING_ENDPOINT'
  | 'REJECTED_INACTIVE_ENDPOINT'
  | 'REJECTED_SELF_PARTNERSHIP'
  | 'REJECTED_CROSS_GRAPH'
  | 'REJECTED_ANCESTRY_EVIDENCE_INDETERMINATE'
  | 'REJECTED_ANCESTRY_CYCLE'
  | 'REJECTED_ANCESTRY_CONFLICT'
  | 'REJECTED_REPLACEMENT_REQUIRES_EXPLICIT_END'
  | 'REJECTED_STALE_ASYMMETRIC_PARTNERSHIP'
  | 'REJECTED_CURRENT_PARTNERSHIP_REQUIRED'
  | 'REJECTED_PARTICIPANT_AUTHORITY_REQUIRED'
  | 'REJECTED_UNCLAIMED_ENDPOINT_ASSERTION_AUTHORITY_REQUIRED'
  | 'REJECTED_STALE_ACTIVE_PARTNERSHIP_WORKFLOW'
  | 'REJECTED_MUTUAL_CONSENT_REQUIRED'
  | 'REJECTED_END_STATUS_REQUIRED';

export interface TreePartnershipLifecycleMemberEvidence {
  readonly id: string;
  readonly ownerUserId: string;
  readonly claimedByUserId?: string | null;
  readonly createdByUserId?: string | null;
  readonly spouseId?: string | null;
  readonly parentIds?: readonly string[];
  readonly approvalStatus?: string | null;
  readonly profileState?: string | null;

  readonly relationshipWorkflow?: {
    readonly kind?: string | null;
    readonly connectorMemberId?: string | null;
    readonly secondaryConnectorMemberId?: string | null;
    readonly partnershipStatus?: string | null;
  } | null;
}

export interface TreePartnershipLifecyclePlanInput {
  readonly members:
    readonly TreePartnershipLifecycleMemberEvidence[];

  readonly actorUserId:
    string | null;

  readonly viewerMemberId:
    string | null;

  readonly personAId: string;
  readonly personBId: string;

  readonly intent:
    TreePartnershipLifecycleIntent;

  readonly endStatus?:
    TreePartnershipLifecycleEndStatus | null;
}

export interface TreePartnershipLifecycleHistoryProposal {
  readonly ownerUserId: string;
  readonly personAId: string;
  readonly personBId: string;
  readonly relationshipKind:
    'spouse-partner';
  readonly status:
    TreePartnershipLifecycleEndStatus;
  readonly sharedChildIds:
    readonly string[];
}

export interface TreePartnershipLifecyclePlan {
  readonly authorized: boolean;
  readonly status:
    TreePartnershipLifecyclePlanStatus;

  /**
   * Planning and execution are intentionally separate.
   *
   * This authority never grants database execution.
   */
  readonly executionAllowed: false;

  readonly actorUserId:
    string | null;

  readonly ownerUserId:
    string | null;

  readonly personAId: string;
  readonly personBId: string;

  readonly expectedSpouseAId:
    string | null;
  readonly expectedSpouseBId:
    string | null;

  readonly proposedSpouseAId:
    string | null;
  readonly proposedSpouseBId:
    string | null;

  readonly sharedChildIds:
    readonly string[];

  readonly preservesParentIds: true;
  readonly preservesPersonIdentity: true;
  readonly personDeletionAuthorized: false;

  readonly requiresMutualConsent:
    boolean;

  readonly staleActiveWorkflowMemberIds:
    readonly string[];

  readonly historyProposal:
    TreePartnershipLifecycleHistoryProposal | null;

  readonly reason: string;
}

const partnershipActiveMember = (
  member:
    TreePartnershipLifecycleMemberEvidence,
): boolean =>
  member.approvalStatus !==
    'rejected' &&
  String(
    member.profileState ?? '',
  ) !== 'deleted';

interface TreePartnershipAncestryInspection {
  readonly ancestors:
    ReadonlySet<string>;
  readonly missingFrontier: boolean;
  readonly cycleDetected: boolean;
}

const inspectPartnershipAncestry = (
  members:
    readonly TreePartnershipLifecycleMemberEvidence[],
  startMemberId: string,
  ownerUserId: string,
): TreePartnershipAncestryInspection => {
  const activeById =
    new Map(
      members
        .filter(
          member =>
            member.ownerUserId ===
              ownerUserId &&
            partnershipActiveMember(
              member,
            ),
        )
        .map(
          member =>
            [member.id, member] as const,
        ),
    );

  const ancestors =
    new Set<string>();

  const visiting =
    new Set<string>();

  const visited =
    new Set<string>();

  let missingFrontier = false;
  let cycleDetected = false;

  const visit = (
    memberId: string,
  ): void => {
    if (visiting.has(memberId)) {
      cycleDetected = true;
      return;
    }

    if (visited.has(memberId)) {
      return;
    }

    const member =
      activeById.get(memberId);

    if (!member) {
      missingFrontier = true;
      return;
    }

    visiting.add(memberId);

    for (
      const parentId of
      member.parentIds ?? []
    ) {
      if (!parentId) {
        continue;
      }

      const parent =
        activeById.get(parentId);

      if (!parent) {
        missingFrontier = true;
        continue;
      }

      if (visiting.has(parentId)) {
        cycleDetected = true;
        continue;
      }

      if (parentId !== startMemberId) {
        ancestors.add(parentId);
      }

      visit(parentId);
    }

    visiting.delete(memberId);
    visited.add(memberId);
  };

  visit(startMemberId);

  return Object.freeze({
    ancestors:
      new Set(ancestors),
    missingFrontier,
    cycleDetected,
  });
};

const canonicalPartnershipPair = (
  personAId: string,
  personBId: string,
): readonly [string, string] =>
  personAId < personBId
    ? [personAId, personBId]
    : [personBId, personAId];

const basePartnershipPlan = (
  input:
    TreePartnershipLifecyclePlanInput,
  overrides:
    Partial<
      Omit<
        TreePartnershipLifecyclePlan,
        | 'executionAllowed'
        | 'preservesParentIds'
        | 'preservesPersonIdentity'
        | 'personDeletionAuthorized'
      >
    > & {
      readonly authorized: boolean;
      readonly status:
        TreePartnershipLifecyclePlanStatus;
      readonly reason: string;
    },
): TreePartnershipLifecyclePlan =>
  Object.freeze({
    authorized:
      overrides.authorized,
    status:
      overrides.status,
    executionAllowed: false,
    actorUserId:
      overrides.actorUserId ??
      input.actorUserId,
    ownerUserId:
      overrides.ownerUserId ??
      null,
    personAId:
      overrides.personAId ??
      input.personAId,
    personBId:
      overrides.personBId ??
      input.personBId,
    expectedSpouseAId:
      overrides.expectedSpouseAId ??
      null,
    expectedSpouseBId:
      overrides.expectedSpouseBId ??
      null,
    proposedSpouseAId:
      overrides.proposedSpouseAId ??
      null,
    proposedSpouseBId:
      overrides.proposedSpouseBId ??
      null,
    sharedChildIds:
      Object.freeze([
        ...(
          overrides.sharedChildIds ??
          []
        ),
      ]),
    preservesParentIds: true,
    preservesPersonIdentity: true,
    personDeletionAuthorized: false,
    requiresMutualConsent:
      overrides.requiresMutualConsent ??
      false,
    staleActiveWorkflowMemberIds:
      Object.freeze([
        ...(
          overrides
            .staleActiveWorkflowMemberIds ??
          []
        ),
      ]),
    historyProposal:
      overrides.historyProposal ??
      null,
    reason:
      overrides.reason,
  });

/**
 * CC-TREE-LIVE-003G3C
 *
 * READ_ONLY_PARTNERSHIP_LIFECYCLE_PLANNING
 *
 * This planner grants zero execution authority.
 *
 * Creation authority:
 * - graph owner may create a partnership between two unclaimed placeholders;
 * - when exactly one endpoint is claimed, only that claimant may assert the
 *   current partnership;
 * - two claimed endpoints require a separately governed mutual-consent
 *   mechanism before creation.
 *
 * Termination authority:
 * - either claimed participant may end an existing reciprocal partnership;
 * - when neither endpoint is claimed, the graph owner may end it;
 * - a graph owner who is not a claimed participant cannot end a partnership
 *   involving a claimed endpoint.
 *
 * Ending partnership never removes parent_ids, co-parent evidence, a person,
 * claimed identity, or other relationship classes.
 */
export function planTreePartnershipLifecycleTransition(
  input:
    TreePartnershipLifecyclePlanInput,
): TreePartnershipLifecyclePlan {
  if (!input.actorUserId) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_AUTH_REQUIRED',
        reason:
          'Authenticated actor identity is required.',
      },
    );
  }

  if (!input.viewerMemberId) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_VIEWER_IDENTITY',
        reason:
          'Viewer member identity is required.',
      },
    );
  }

  const viewer =
    input.members.find(
      member =>
        member.id ===
          input.viewerMemberId &&
        partnershipActiveMember(
          member,
        ),
    ) ?? null;

  if (
    !viewer ||
    viewer.claimedByUserId !==
      input.actorUserId
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_VIEWER_IDENTITY',
        reason:
          'Viewer identity is not bound to the authenticated actor.',
      },
    );
  }

  if (
    !input.personAId ||
    !input.personBId
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_MISSING_ENDPOINT',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Two partnership endpoints are required.',
      },
    );
  }

  if (
    input.personAId ===
    input.personBId
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_SELF_PARTNERSHIP',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'A member cannot be partnered with themselves.',
      },
    );
  }

  const personA =
    input.members.find(
      member =>
        member.id ===
        input.personAId,
    ) ?? null;

  const personB =
    input.members.find(
      member =>
        member.id ===
        input.personBId,
    ) ?? null;

  if (!personA || !personB) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_MISSING_ENDPOINT',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Partnership endpoint is absent from the loaded graph.',
      },
    );
  }

  if (
    !partnershipActiveMember(
      personA,
    ) ||
    !partnershipActiveMember(
      personB,
    )
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_INACTIVE_ENDPOINT',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Partnership endpoints must be active.',
      },
    );
  }

  if (
    personA.ownerUserId !==
      viewer.ownerUserId ||
    personB.ownerUserId !==
      viewer.ownerUserId
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_CROSS_GRAPH',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Partnership endpoints must belong to the viewer canonical graph.',
      },
    );
  }

  const aAncestry =
    inspectPartnershipAncestry(
      input.members,
      personA.id,
      viewer.ownerUserId,
    );

  const bAncestry =
    inspectPartnershipAncestry(
      input.members,
      personB.id,
      viewer.ownerUserId,
    );

  if (
    input.intent === 'CREATE_CURRENT' &&
    (
      aAncestry.missingFrontier ||
      bAncestry.missingFrontier
    )
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_ANCESTRY_EVIDENCE_INDETERMINATE',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Partnership creation requires complete reachable ancestry evidence for both endpoints.',
      },
    );
  }

  if (
    input.intent === 'CREATE_CURRENT' &&
    (
      aAncestry.cycleDetected ||
      bAncestry.cycleDetected
    )
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_ANCESTRY_CYCLE',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'Partnership creation is blocked while reachable ancestry contains a cycle.',
      },
    );
  }

  if (
    input.intent === 'CREATE_CURRENT' &&
    (
      aAncestry.ancestors.has(
        personB.id,
      ) ||
      bAncestry.ancestors.has(
        personA.id,
      )
    )
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_ANCESTRY_CONFLICT',
        ownerUserId:
          viewer.ownerUserId,
        reason:
          'A partnership cannot overlap direct ancestry.',
      },
    );
  }

  const sharedChildIds =
    input.members
      .filter(
        member =>
          member.ownerUserId ===
            viewer.ownerUserId &&
          partnershipActiveMember(
            member,
          ) &&
          (
            member.parentIds ?? []
          ).includes(personA.id) &&
          (
            member.parentIds ?? []
          ).includes(personB.id),
      )
      .map(member => member.id)
      .sort();

  const activePartnershipWorkflowEvidence =
    input.members
      .filter(member => {
        if (
          member.id !== personA.id &&
          member.id !== personB.id
        ) {
          return false;
        }

        const workflow =
          member.relationshipWorkflow;

        return Boolean(
          workflow &&
          (
            workflow.kind ===
              'spouse-partner' ||
            workflow.kind ===
              'former-spouse-partner'
          ) &&
          workflow.partnershipStatus ===
            'current',
        );
      });

  const staleActiveWorkflowMemberIds =
    activePartnershipWorkflowEvidence
      .map(member => member.id)
      .sort();

  const conflictingActiveWorkflowMemberIds =
    activePartnershipWorkflowEvidence
      .filter(member => {
        const workflow =
          member.relationshipWorkflow;

        const expectedOtherId =
          member.id === personA.id
            ? personB.id
            : personA.id;

        const connectors =
          [
            workflow
              ?.connectorMemberId,
            workflow
              ?.secondaryConnectorMemberId,
          ].filter(Boolean);

        return !connectors.includes(
          expectedOtherId,
        );
      })
      .map(member => member.id)
      .sort();

  const aClaim =
    personA.claimedByUserId ??
    null;

  const bClaim =
    personB.claimedByUserId ??
    null;

  const bothClaimed =
    Boolean(aClaim && bClaim);

  const eitherClaimed =
    Boolean(aClaim || bClaim);

  const actorClaimsA =
    aClaim ===
    input.actorUserId;

  const actorClaimsB =
    bClaim ===
    input.actorUserId;

  const actorIsParticipant =
    actorClaimsA ||
    actorClaimsB;

  const actorIsGraphOwner =
    viewer.ownerUserId ===
    input.actorUserId;

  const currentA =
    personA.spouseId ??
    null;

  const currentB =
    personB.spouseId ??
    null;

  if (
    input.intent ===
    'CREATE_CURRENT'
  ) {
    if (
      currentA ===
        personB.id &&
      currentB ===
        personA.id
    ) {
      if (
        conflictingActiveWorkflowMemberIds.length >
          0
      ) {
        return basePartnershipPlan(
          input,
          {
            authorized: false,
            status:
              'REJECTED_STALE_ACTIVE_PARTNERSHIP_WORKFLOW',
            ownerUserId:
              viewer.ownerUserId,
            expectedSpouseAId:
              personB.id,
            expectedSpouseBId:
              personA.id,
            sharedChildIds,
            staleActiveWorkflowMemberIds,
            reason:
              'A reciprocal current partnership conflicts with another active partnership workflow on an endpoint.',
          },
        );
      }

      return basePartnershipPlan(
        input,
        {
          authorized: true,
          status:
            'ALREADY_CURRENT',
          ownerUserId:
            viewer.ownerUserId,
          expectedSpouseAId:
            personB.id,
          expectedSpouseBId:
            personA.id,
          proposedSpouseAId:
            personB.id,
          proposedSpouseBId:
            personA.id,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'The reciprocal current partnership already exists.',
        },
      );
    }

    if (
      (
        currentA === personB.id &&
        currentB !== personA.id
      ) ||
      (
        currentB === personA.id &&
        currentA !== personB.id
      )
    ) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_STALE_ASYMMETRIC_PARTNERSHIP',
          ownerUserId:
            viewer.ownerUserId,
          expectedSpouseAId:
            currentA,
          expectedSpouseBId:
            currentB,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'One-sided partnership evidence must be reconciled before creation.',
        },
      );
    }

    if (
      currentA !== null ||
      currentB !== null
    ) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_REPLACEMENT_REQUIRES_EXPLICIT_END',
          ownerUserId:
            viewer.ownerUserId,
          expectedSpouseAId:
            currentA,
          expectedSpouseBId:
            currentB,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'Existing partnerships must be ended explicitly before a different current partnership is created.',
        },
      );
    }

    if (
      staleActiveWorkflowMemberIds.length > 0
    ) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_STALE_ACTIVE_PARTNERSHIP_WORKFLOW',
          ownerUserId:
            viewer.ownerUserId,
          expectedSpouseAId:
            currentA,
          expectedSpouseBId:
            currentB,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'Active spouse workflow evidence without the reciprocal current spouse pair must be reconciled before creation.',
        },
      );
    }

    if (bothClaimed) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_MUTUAL_CONSENT_REQUIRED',
          ownerUserId:
            viewer.ownerUserId,
          expectedSpouseAId:
            null,
          expectedSpouseBId:
            null,
          proposedSpouseAId:
            personB.id,
          proposedSpouseBId:
            personA.id,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          requiresMutualConsent:
            true,
          reason:
            'Creating a current partnership between two claimed profiles requires separately governed mutual consent.',
        },
      );
    }

    if (
      eitherClaimed &&
      !actorIsParticipant
    ) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_PARTICIPANT_AUTHORITY_REQUIRED',
          ownerUserId:
            viewer.ownerUserId,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'A claimed endpoint requires its claimant to assert the partnership.',
        },
      );
    }

    if (eitherClaimed) {
      const unclaimedEndpoint =
        aClaim
          ? personB
          : personA;

      const actorControlsUnclaimedEndpoint =
        actorIsGraphOwner ||
        unclaimedEndpoint
          .createdByUserId ===
          input.actorUserId;

      if (!actorControlsUnclaimedEndpoint) {
        return basePartnershipPlan(
          input,
          {
            authorized: false,
            status:
              'REJECTED_UNCLAIMED_ENDPOINT_ASSERTION_AUTHORITY_REQUIRED',
            ownerUserId:
              viewer.ownerUserId,
            sharedChildIds,
            staleActiveWorkflowMemberIds,
            reason:
              'The claimant may assert a partnership with an unclaimed placeholder only when that placeholder was created by the actor or is managed by the graph owner.',
          },
        );
      }
    }

    if (
      !eitherClaimed &&
      !actorIsGraphOwner
    ) {
      return basePartnershipPlan(
        input,
        {
          authorized: false,
          status:
            'REJECTED_PARTICIPANT_AUTHORITY_REQUIRED',
          ownerUserId:
            viewer.ownerUserId,
          sharedChildIds,
          staleActiveWorkflowMemberIds,
          reason:
            'Only the graph owner may create a partnership between two unclaimed placeholders.',
        },
      );
    }

    return basePartnershipPlan(
      input,
      {
        authorized: true,
        status:
          'AUTHORIZED_CREATE_PLAN',
        ownerUserId:
          viewer.ownerUserId,
        expectedSpouseAId:
          null,
        expectedSpouseBId:
          null,
        proposedSpouseAId:
          personB.id,
        proposedSpouseBId:
          personA.id,
        sharedChildIds,
        staleActiveWorkflowMemberIds,
        reason:
          'Current partnership creation is eligible for a separately governed atomic writer.',
      },
    );
  }

  if (
    currentA !==
      personB.id ||
    currentB !==
      personA.id
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          (
            currentA ===
              personB.id ||
            currentB ===
              personA.id
          )
            ? 'REJECTED_STALE_ASYMMETRIC_PARTNERSHIP'
            : 'REJECTED_CURRENT_PARTNERSHIP_REQUIRED',
        ownerUserId:
          viewer.ownerUserId,
        expectedSpouseAId:
          currentA,
        expectedSpouseBId:
          currentB,
        sharedChildIds,
        staleActiveWorkflowMemberIds,
        reason:
          'Ending a partnership requires an exact reciprocal current partnership before-state.',
      },
    );
  }

  if (!input.endStatus) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_END_STATUS_REQUIRED',
        ownerUserId:
          viewer.ownerUserId,
        expectedSpouseAId:
          personB.id,
        expectedSpouseBId:
          personA.id,
        sharedChildIds,
        staleActiveWorkflowMemberIds,
        reason:
          'A governed historical end status is required.',
      },
    );
  }

  if (
    eitherClaimed &&
    !actorIsParticipant
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_PARTICIPANT_AUTHORITY_REQUIRED',
        ownerUserId:
          viewer.ownerUserId,
        expectedSpouseAId:
          personB.id,
        expectedSpouseBId:
          personA.id,
        sharedChildIds,
        staleActiveWorkflowMemberIds,
        reason:
          'A partnership involving a claimed profile may be ended only by a claimed participant.',
      },
    );
  }

  if (
    !eitherClaimed &&
    !actorIsGraphOwner
  ) {
    return basePartnershipPlan(
      input,
      {
        authorized: false,
        status:
          'REJECTED_PARTICIPANT_AUTHORITY_REQUIRED',
        ownerUserId:
          viewer.ownerUserId,
        expectedSpouseAId:
          personB.id,
        expectedSpouseBId:
          personA.id,
        sharedChildIds,
        staleActiveWorkflowMemberIds,
        reason:
          'Only the graph owner may end a partnership between two unclaimed placeholders.',
      },
    );
  }

  const [
    historyA,
    historyB,
  ] =
    canonicalPartnershipPair(
      personA.id,
      personB.id,
    );

  return basePartnershipPlan(
    input,
    {
      authorized: true,
      status:
        'AUTHORIZED_END_PLAN',
      ownerUserId:
        viewer.ownerUserId,
      expectedSpouseAId:
        personB.id,
      expectedSpouseBId:
        personA.id,
      proposedSpouseAId:
        null,
      proposedSpouseBId:
        null,
      sharedChildIds,
      staleActiveWorkflowMemberIds,
      historyProposal:
        Object.freeze({
          ownerUserId:
            viewer.ownerUserId,
          personAId:
            historyA,
          personBId:
            historyB,
          relationshipKind:
            'spouse-partner',
          status:
            input.endStatus,
          sharedChildIds:
            Object.freeze(
              [...sharedChildIds],
            ),
        }),
      reason:
        'Current partnership termination is eligible for a separately governed atomic writer; parentage, co-parent evidence, and person identity remain untouched.',
    },
  );
}
